import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Select } from '@wds/select';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import HelpdeskForm from 'sma.helpdesk/form/form';
import options from 'sma.helpdesk/support_options';
import CategoryPicker from './components/categoryPicker';
import messagingEnum, {
	LockoutMessage,
	RegOtherMessage,
	FeatureSuggestionsMessage,
	CSSMessage,
	HowToMessage,
	SomeoneElseMessage,
	ContactAdmin,
} from '../messaging';
import getComponentWithTheme from '../../themeProvider';

const isCategoryVisible = (category, loggedIn, isApplicant, isReviewer, isRecommender, isAdmin, isPro) => {
	const visibleToSite = true; // used to be a flag used for FR vs Apply
	const visibleToPackage = isPro || !category.requiresProPackage;
	const visibleToUsersRole = !category.roles // If no roles defined, show the category
		|| ( // Otherwise, check if roles match
			category.roles.includes('all')
			|| (!loggedIn && category.roles.includes('loggedOut'))
			|| (loggedIn && category.roles.includes('loggedIn'))
			|| (isApplicant && category.roles.includes('applicants'))
			|| (isReviewer && category.roles.includes('reviewers'))
			|| (isRecommender && category.roles.includes('recommenders'))
			|| (isAdmin && category.roles.includes('admin'))
		);
	return visibleToSite && visibleToPackage && visibleToUsersRole;
};

const HelpdeskPage = (props) => {
	const [helpOption, setHelpOption] = useState('');
	const [categoryList, setCategoryList] = useState([]);
	const [category, setCategory] = useState(null);
	const [displayThankYouPage, setDisplayThankYouPage] = useState(false);
	const [fieldsToDisplay, setFieldsToDisplay] = useState({});

	const {
		loggedIn,
		isApplicant,
		isReviewer,
		isRecommender,
		isAdmin,
		isEnterprisePlus,
		canRequestCall,
	} = props;

	useEffect(() => {
		const { isPro } = props;
		setCategoryList(options.categories
			.filter((header) => isCategoryVisible(header, loggedIn, isApplicant, isReviewer, isRecommender, isAdmin, isPro))
			.map((header) => {
				if (!header.children) {
					return header;
				}
				return {
					...header,
					label: header.label,
					children: header.children
						.filter((ctg) => isCategoryVisible(ctg, loggedIn, isApplicant, isReviewer, isRecommender, isAdmin, isPro))
						.map((pa) => ({
							...pa,
							label: pa.label,
						}))
						// Alphabetically sort children
						.sort((a, b) => {
							if (!(a.sortLast && b.sortLast)) {
								if (b.sortLast) {
									return -1;
								}
								if (a.sortLast) {
									return 1;
								}
							}
							if (a.label < b.label) {
								return -1;
							}
							if (a.label > b.label) {
								return 1;
							}
							return 0;
						}),
				};
			})
			// Filter out any headers with no remaining children
			.filter((header) => !header.children || header.children.length > 0)
			// Alphabetically sort headers
			.sort((a, b) => {
				if (a.label < b.label) {
					return -1;
				}
				if (a.label > b.label) {
					return 1;
				}
				return 0;
			}));
	}, []);

	useEffect(() => {
		let newFieldsToDisplay = { nameFields: !loggedIn };

		if (loggedIn && category) {
			newFieldsToDisplay = {
				...newFieldsToDisplay,
				relatedPrograms: !category.relatedPrograms ? null : { required: category.relatedPrograms.required },
				relatedStages: !category.relatedStages ? null : { required: category.relatedStages.required },
				relatedTasks: !category.relatedTasks ? null : { required: category.relatedTasks.required },
				relatedUsers: !category.relatedUsers ? null : { required: category.relatedUsers.required },
				relatedApplications: !category.relatedApplications ? null : { required: category.relatedApplications.required },
				relatedReports: !category.relatedReports ? null : { required: category.relatedReports.required },
				relatedAutomations: !category.relatedAutomations ? null : { required: category.relatedAutomations.required },
				sslFields: !category.sslFields ? null : { required: category.sslFields.required },
			};
		}

		setFieldsToDisplay(newFieldsToDisplay);
	}, [category]);

	const renderMessage = (message) => (
		<div className='sma-helpdesk-messaging'>
			{message === messagingEnum.lockoutMessage && (<LockoutMessage />)}
			{message === messagingEnum.regOtherMessage && (<RegOtherMessage />)}
			{message === messagingEnum.featureSuggestionsMessage && (
				<FeatureSuggestionsMessage />
			)}
			{message === messagingEnum.cssMessage && (<CSSMessage />)}
			{message === messagingEnum.howToMessage && (<HowToMessage />)}
			{message === messagingEnum.someoneElseMessage && (<SomeoneElseMessage />)}
			{message === messagingEnum.ContactAdmin && (<ContactAdmin />)}
		</div>
	);

	const supportHours = (
		<p
			// dangerouslySetInnerHTML is normally a warning, but here we
			// are using controlled text therefore it is safe to use.
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={
				{
					__html: gettext(
						'Our support hours are <strong>Monday to Friday</strong> from '
						+ '<strong>8am - 8pm Eastern Time</strong>, <strong>Sundays</strong> '
						+ 'from <strong>10am - 2pm Eastern Time</strong>.',
					),
				}
			}
		/>
	);

	return displayThankYouPage ? (
		<Box>
			<Box mb={3}>
				<Typography align='center' component='h1' variant='sectionTitle'>
					{gettext('Thank you!')}
				</Typography>
			</Box>
			<Typography align='center'>
				{gettext(
					'Our SurveyMonkey Apply support team will review your question and '
					+ 'get back to you as soon as possible.',
				)}
			</Typography>
			<Typography align='center'>{supportHours}</Typography>
		</Box>
	) : (
		<div>
			<div>
				<Typography
					align='center'
					component='h1'
					variant='sectionTitle'
				>
					{gettext('Apply Customer Support')}
				</Typography>
				<Typography align='center'>
					{gettext(
						'Our speedy and knowledgeable SurveyMonkey Apply '
						+ 'support team will help you find an answer.',
					)}
				</Typography>
				<Typography align='center'>{supportHours}</Typography>
			</div>
			<hr />
			<Box className='wds-card' mt={3} p={6}>
				<h3>{gettext('What can we help you with?')}</h3>
				<Box mb={3}>
					<Select
						stretched
						onChange={(e) => {
							const targetValue = e.target.value;
							let newHelpOption;
							if (isAdmin) {
								newHelpOption = options.adminHelpOptions[targetValue];
							} else if (!isAdmin && loggedIn) { // logged in, non admin
								newHelpOption = options.nonAdminHelpOptions[targetValue];
							} else if (!loggedIn) { // logged out
								newHelpOption = options.loggedOutHelpOptions[targetValue];
							}
							setCategory(null); // Reset the category picker
							if (newHelpOption.category) {
								setCategory(newHelpOption.category);
							} else {
								setCategory(null); // Reset the category picker
							}
							setHelpOption(newHelpOption);
						}}
					>
						{
							isAdmin && (
								options.adminHelpOptions.map((option, idx) => (
									<Select.Option
										value={idx}
										key={option.value}
									>
										{option.string}
									</Select.Option>
								)))

							|| !isAdmin && loggedIn && (
								options.nonAdminHelpOptions.map((option, idx) => (
									<Select.Option
										value={idx}
										key={option.value}
									>
										{option.string}
									</Select.Option>
								)))

							|| !loggedIn && (
								options.loggedOutHelpOptions.map((option, idx) => (
									<Select.Option
										value={idx}
										key={option.value}
									>
										{option.string}
									</Select.Option>
								)))
						}
					</Select>
				</Box>
				{
					helpOption && !helpOption.disableCategories && (
						<CategoryPicker
							className='mb-3'
							categoryList={categoryList}
							category={category}
							setCategory={(ctg) => {
								setCategory(ctg);
							}}
						/>
					)
				}
				{
					helpOption && helpOption.message && renderMessage(helpOption.message)
				}
				{
					category && category.message && renderMessage(category.message)
				}
				{
					category && category.formLink && (
						<HelpdeskForm
							fieldsToDisplay={fieldsToDisplay}
							isEnterprisePlus={isEnterprisePlus}
							userType={
								(isAdmin && 'administrator')
								|| (isReviewer && 'reviewer')
								|| (isApplicant && 'applicant')
								|| (isRecommender && 'recommender')
								|| ''
							}
							requestType={helpOption.zendeskValue}
							categorization={category.zendeskValue}
							browserVersion={navigator.userAgent}
							callEligible={!!(
								canRequestCall && isAdmin && helpOption.phoneSupport
								&& category.adminCanRequestCall
							)}
							successCallback={() => setDisplayThankYouPage(true)}
						/>
					)
				}
			</Box>
		</div>
	);
};

HelpdeskPage.propTypes = {
	loggedIn: PropTypes.bool.isRequired,
	isApplicant: PropTypes.bool.isRequired,
	isReviewer: PropTypes.bool.isRequired,
	isRecommender: PropTypes.bool.isRequired,
	isAdmin: PropTypes.bool.isRequired,
	isEnterprisePlus: PropTypes.bool.isRequired,
	canRequestCall: PropTypes.bool.isRequired,
};

export default function renderHelpdeskPage(props) {
	const element = React.createElement(HelpdeskPage, props); // gets the props that are passed in the template
	ReactDOM.render(
		getComponentWithTheme(
			<>
				{element}
			</>,
			window?.LOCALE?.theme,
		),
		props.react_mount, // a reference to the #react div that we render to
	);
}
